import {
  FunctionDefinition,
  T,
  OneOfT,
  FunctionSignature,
  FunctionArgument,
  isVariadic,
  VariadicT,
  isOneOfT,
  OptionalT,
  isOptionalT,
} from '../library/definition';

// Header

export function generateFnHeader(
  name: string,
  args: FunctionDefinition['arguments'] | undefined,
  signatures: readonly FunctionSignature[],
) {
  return `${name}(${generateFnHeaderArgs(args, signatures)})`;
}

export function generateFnHeaderArgs(
  args: FunctionDefinition['arguments'] | undefined,
  signatures: readonly FunctionSignature[],
) {
  return args === undefined ? generateFnHeaderArgsfromSignatures(signatures) : generateFnHeaderArgsFromArguments(args);
}

function generateFnHeaderArgsFromArguments(args: FunctionDefinition['arguments']) {
  return args
    ?.map((arg) =>
      'variadic' in arg
        ? `${generateFnHeaderArgList(arg.variadic, '1')}, ${wrapVariadic(generateFnHeaderArgList(arg.variadic, '2'))}`
        : generateFnHeaderArg(arg),
    )
    .join(', ');
}

function generateFnHeaderArgsfromSignatures(signatures: readonly FunctionSignature[]) {
  const argCounts = signatures.filter((s) => !s.arguments.some(isVariadic)).map((s) => s.arguments.length);
  const maxArgs = Math.max(0, ...argCounts);
  const minArgs = Math.min(maxArgs, ...argCounts);
  const hasVariadic = signatures.some((s) => s.arguments.some(isVariadic));

  if (maxArgs === 0 && hasVariadic) {
    // Just show variadic arguments
    return generateFnHeaderArgsFromArguments([{ variadic: [{ name: 'value' }] }]);
  }

  // Show arguments, possibly suffixed with variadic notation if any present

  const shouldNumberArgs = maxArgs !== 1 || hasVariadic;
  const getDummyName = (i: number) => (shouldNumberArgs ? `value${i + 1}` : 'value');

  const mandatoryArgs = new Array(minArgs).fill(null).map((_, i) => ({ name: getDummyName(i) }));
  const optionalArgs = new Array(maxArgs - minArgs)
    .fill(null)
    .map((_, i) => ({ name: wrapOptional(getDummyName(i + minArgs), true) }));

  const totalArgs = mandatoryArgs.length + optionalArgs.length;
  const argsList = generateFnHeaderArgList([...mandatoryArgs, ...optionalArgs]);
  const variadicNotation = hasVariadic ? wrapVariadic(getDummyName(totalArgs)) : null;

  return `${argsList}${variadicNotation === null ? '' : `, ${variadicNotation}`}`;
}

function generateFnHeaderArg(arg: FunctionArgument) {
  return wrapOptional(arg.name, arg.optional);
}

function generateFnHeaderArgList(args: FunctionArgument[], suffix = '') {
  return args.map((arg) => generateFnHeaderArg({ ...arg, name: `${arg.name}${suffix}` })).join(', ');
}

// Arguments list

export function generateFnArguments(args?: FunctionDefinition['arguments']) {
  if (args === undefined) {
    return null;
  }
  return args
    .map((arg) =>
      'variadic' in arg ? arg.variadic.map((a) => generateFnArgument(a)).join('\n') : generateFnArgument(arg),
    )
    .flat()
    .join('\n');
}

function generateFnArgument(arg: FunctionArgument) {
  return `- \`${arg.name}\`${arg.optional === true ? ' (Optional)' : ''} ${arg.description}${
    arg.values !== undefined ? ` (allowed values: ${generateAllowedValues(arg.values)})` : ''
  }`;
}

function generateAllowedValues(values: readonly string[]) {
  return values.map((v) => `\`${v}\``).join(', ');
}

// Signatures list

export function generateFnSignatures(name: string, signatures: readonly FunctionSignature[]) {
  if (signatures.length === 0) {
    return null;
  }
  if (signatures.length === 1) {
    return `\`${generateFnSignature(name, signatures[0])}\``;
  }

  return signatures.map((signature) => `- \`${generateFnSignature(name, signature)}\``).join('\n');
}

export function generateFnSignature(name: string, signature: FunctionSignature) {
  const argumentsStr = generateFnArgumentsSignature(signature);

  return `${name}(${argumentsStr}): ${signature.returnType ?? 'Unknown'}`;
}

function generateFnArgumentsSignature(signature: FunctionSignature) {
  return `${signature.arguments.map((arg) => generateFnArgTypes(arg)).join(', ')}`;
}

function generateFnVariadicSignature(signature: VariadicT): string {
  const params = `${signature.$variadic.map(generateFnArgTypes).join(', ')}`;
  return `${params}, ${wrapVariadic(params)}`;
}

function generateFnArgTypes(arg: T | OneOfT | VariadicT | OptionalT): string {
  return isVariadic(arg)
    ? generateFnVariadicSignature(arg)
    : isOptionalT(arg)
    ? `${generateFnArgTypes(arg.$optional)}?`
    : isOneOfT(arg)
    ? `${arg.$oneOf.join(' | ')}`
    : `${arg}`;
}

export function wrapOptional(name: string, optional?: boolean) {
  return optional === true ? `[${name}]` : name;
}

export function wrapVariadic(name: string) {
  return `[${name}, ...]`;
}

// Misc

export function getStringLiterals(values: string[]) {
  return values.map((v) => `'${v}'`);
}
